import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError } from '@/lib/chunk-recovery';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The last line of defence for a dynamic-import failure (§3d).
 *
 * `loadChunk` already retries transient failures and reloads on a genuine 404,
 * so anything reaching here is either a real application error or a chunk that
 * failed every retry. Both get a visible, actionable screen rather than a
 * blank page — the failure mode this whole mechanism exists to prevent.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    const chunk = isChunkLoadError(error);

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-lg font-semibold">
            {chunk ? 'This page could not be loaded' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {chunk
              ? 'The application may have been updated while this tab was open. Reloading will ' +
                'pick up the new version.'
              : 'The page hit an unexpected error. Reloading usually clears it.'}
          </p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    );
  }
}
